import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { _nextTrackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  SKIP_FROM_SUMMARY_STEP: '3419',
  DOWNLOAD_PDF: '3420',
  LEARN_MORE: '3421',
  CONTINUED_TO_LEASES: '3422',
  TIME_SPENT: '3423',
  DEACTIVATE: '4034',
  DYNAMIC_NOTIFY_LEADS: '4035',
  CONFIRM_UPLOAD_SIGNED_LEASE: '4754',
  DECLINE_UPLOAD_SIGNED_LEASE: '4755',
  CONFIRM_UPLOAD_UNSIGNED_LEASE: '4756',
  DECLINE_UPLOAD_UNSIGNED_LEASE: '4757',
  CONFIRM_CREATE_CUSTOM_LEASE: '4758',
  DECLINE_CREATE_CUSTOM_LEASE: '4759',
} as const;

export const TRIGGER_SOURCE_MAP = {
  SKIP: 'skip',
  DELETE: 'delete',
  NOTIFY: 'notify',
  ADD: 'add',
} as const;

type KeysOfDynamicNextFromTriggerSource = keyof typeof TRIGGER_SOURCE_MAP;
type ValuesOfDynamicNextFromTriggerSource = (typeof TRIGGER_SOURCE_MAP)[KeysOfDynamicNextFromTriggerSource];

// https://dataportal.zillowgroup.net/event-registry/3419
export const emitSkipFromSummaryStepEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.SKIP_FROM_SUMMARY_STEP,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'summary_deactivation_screen',
      trigger_source_nm: 'skip',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3420
export const emitDownloadPDFEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DOWNLOAD_PDF,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'summary_deactivation_screen',
      trigger_source_nm: 'download_pdf',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3421
export const emitLearnMoreEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.LEARN_MORE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'summary_deactivation_screen',
      trigger_source_nm: 'learn_more',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3422
export const emitContinuedToLeasesEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONTINUED_TO_LEASES,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'summary_deactivation_screen',
      trigger_source_nm: 'next',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4034
export const emitDeactivateEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DEACTIVATE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'button_to_deactivate',
    },
    semantic: {
      semantic_event_nm: 'deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4035
export const emitDynamicNotifyLeadsEvent = ({
  currentProperty,
  user,
  triggerSource,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerSource: ValuesOfDynamicNextFromTriggerSource;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DYNAMIC_NOTIFY_LEADS,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'renter_notification_screen',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4754
export const emitConfirmUploadSignedLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONFIRM_UPLOAD_SIGNED_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'upload_your_signed_lease_screen',
      trigger_source_nm: 'upload_lease',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4755
export const emitDeclineUploadSignedLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DECLINE_UPLOAD_SIGNED_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'upload_your_signed_lease_screen',
      trigger_source_nm: 'no_thanks',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4756
export const emitConfirmUploadUnsignedLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONFIRM_UPLOAD_UNSIGNED_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'upload_lease_for_signing_screen',
      trigger_source_nm: 'upload_lease',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4757
export const emitDeclineUploadUnsignedLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DECLINE_UPLOAD_UNSIGNED_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'upload_lease_for_signing_screen',
      trigger_source_nm: 'no_thanks',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4758
export const emitConfirmCreateCustomLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONFIRM_CREATE_CUSTOM_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'create_custom_lease_screen',
      trigger_source_nm: 'create_lease',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4759
export const emitDeclineCreateCustomLeaseEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  _nextTrackEvent.listingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DECLINE_CREATE_CUSTOM_LEASE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'listing_deactivation_flow',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'create_custom_lease_screen',
      trigger_source_nm: 'no_thanks',
    },
    semantic: {
      semantic_event_nm: 'continue_deactivation',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
